import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Award,
  BriefcaseBusiness,
  Building2,
  CheckCircle2,
  Clock3,
  Image as ImageIcon,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
} from "lucide-react";
import { Container } from "react-bootstrap";
import api from "Common/api";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { ContractorDetailsSkeleton } from "components/shimmer";
import "./contractor-profile.css";

const RELIBUILD_LOGO =
  "https://tradesmen.s3.amazonaws.com/relibuild-logo/relibuild-logo.png";

const clean = (value: any) => {
  const text = String(value ?? "").trim();
  return text && text !== "null" && text !== "undefined" ? text : "";
};

const parseValue = (value: any) => {
  if (!value) return null;
  if (typeof value === "object") return value;

  try {
    return JSON.parse(value);
  } catch (_error) {
    return null;
  }
};

const parseLabels = (value: any) => {
  const parsed = parseValue(value);
  const values = Array.isArray(parsed) ? parsed : parsed ? [parsed] : [];

  return values
    .map((item: any) =>
      typeof item === "string" ? item : item?.label || item?.value || ""
    )
    .filter(Boolean);
};

const joinAddress = (...parts: any[]) => parts.map(clean).filter(Boolean).join(", ");

export function RecentContractorProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [contractor, setContractor] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const openedFromSuggestions = new URLSearchParams(location.search).get("from") === "suggested-contractors";
  const backPath = openedFromSuggestions ? "/suggested-contractors" : "/recent-contractors";
  const backLabel = openedFromSuggestions ? "Suggested Contractors" : "Recent Contractors";

  const loadProfile = useCallback(() => {
    if (!id) {
      setError("Contractor information is unavailable.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    api
      .get(`/user/contractor-profile/${id}`)
      .then((response: any) => setContractor(response?.data || null))
      .catch((requestError: any) => {
        setError(
          requestError?.response?.data?.message ||
            "Unable to load this contractor profile."
        );
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const skills = useMemo(() => parseLabels(contractor?.ua_skills), [contractor]);
  const profile = useMemo(() => parseLabels(contractor?.ua_profile)[0] || "Contractor", [contractor]);
  const availability = useMemo(
    () => parseLabels(contractor?.ua_work_availbility)[0] || "Not provided",
    [contractor]
  );
  const recentWorks = Array.isArray(contractor?.recent_works)
    ? contractor.recent_works
    : [];
  const name = [contractor?.user_first_name, contractor?.user_last_name]
    .map(clean)
    .filter(Boolean)
    .join(" ");
  const address = joinAddress(
    contractor?.cc_address || contractor?.ua_address,
    contractor?.cc_city || contractor?.ua_city,
    contractor?.cc_state || contractor?.ua_state,
    contractor?.cc_zip_code || contractor?.ua_zip_code,
    contractor?.cc_country || contractor?.ua_country
  );

  useEffect(() => {
    document.title = name ? `${name} | RELiBUILD` : "Contractor Profile | RELiBUILD";
  }, [name]);

  if (loading) {
    return (
      <div className="page-content tailwind-prefix contractor-profile-page">
        <Container fluid>
          <ContractorDetailsSkeleton />
        </Container>
      </div>
    );
  }

  if (error || !contractor) {
    return (
      <div className="page-content tailwind-prefix contractor-profile-page">
        <Container fluid>
          <div className="contractor-profile__error">
            <ShieldCheck aria-hidden="true" />
            <h2>Could not open profile</h2>
            <p>{error}</p>
            <div>
              <Button variant="outline" onClick={() => navigate(backPath)}>
                Back
              </Button>
              <Button onClick={loadProfile}>Try again</Button>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content tailwind-prefix contractor-profile-page">
      <Container fluid>
        <button
          type="button"
          onClick={() => navigate(backPath)}
          className="contractor-profile__back"
        >
          <ArrowLeft aria-hidden="true" />
          {backLabel}
        </button>

        <section className="contractor-profile__hero">
          <div className="contractor-profile__cover">
            {contractor.ua_cover_pic ? (
              <>
                <img src={contractor.ua_cover_pic} alt="" className="contractor-profile__cover-backdrop" aria-hidden="true" />
                <img src={contractor.ua_cover_pic} alt="Company cover" className="contractor-profile__cover-image" />
              </>
            ) : null}
          </div>
          <div className="contractor-profile__identity">
            <div className="contractor-profile__identity-main">
              <img
                src={contractor.ua_profile_pic || contractor.cc_logo_url || RELIBUILD_LOGO}
                alt={name || "Contractor"}
                className="contractor-profile__avatar"
              />
              <div className="contractor-profile__name">
                <span className="contractor-profile__eyebrow"><ShieldCheck aria-hidden="true" /> Contractor profile</span>
                <h1>{name || "Contractor"}</h1>
                <div className="contractor-profile__meta">
                  <span><BriefcaseBusiness aria-hidden="true" /> {profile}</span>
                  {clean(contractor.cc_company_name) ? <span><Building2 aria-hidden="true" /> {clean(contractor.cc_company_name)}</span> : null}
                  {address ? <span><MapPin aria-hidden="true" /> {address}</span> : null}
                </div>
              </div>
            </div>
            <span className="contractor-profile__availability"><i aria-hidden="true" /> {availability}</span>
          </div>
        </section>

        <div className="contractor-profile__layout">
          <main className="contractor-profile__main">
            <section className="contractor-profile__panel contractor-profile__about">
              <div className="contractor-profile__section-heading">
                <div><span>Background</span><h2>About this contractor</h2></div>
              </div>
              <p className="contractor-profile__description">
                {clean(contractor.ua_description) || "No profile description provided."}
              </p>
              <div className="contractor-profile__stats">
                <ProfileStat icon={Award} label="Experience" value={clean(contractor.ua_exp) || "N/A"} />
                <ProfileStat icon={CheckCircle2} label="Completed jobs" value={clean(contractor.ua_completed) || "0"} />
                <ProfileStat icon={Clock3} label="Working hours" value={clean(contractor.ua_work_hours) || "N/A"} />
              </div>
            </section>

            <section className="contractor-profile__panel">
              <div className="contractor-profile__section-heading">
                <div><span>Expertise</span><h2>Skills and services</h2></div>
              </div>
              <div className="contractor-profile__skills">
                {skills.length ? (
                  skills.map((skill: string) => <Badge key={skill} variant="secondary">{skill}</Badge>)
                ) : (
                  <p className="contractor-profile__empty-copy">No skills provided.</p>
                )}
              </div>
            </section>

            <section className="contractor-profile__panel">
              <div className="contractor-profile__section-heading">
                <div><span>Portfolio</span><h2>Recent work</h2></div>
                {recentWorks.length ? <p>{recentWorks.length} {recentWorks.length === 1 ? "project" : "projects"}</p> : null}
              </div>
              {recentWorks.length ? (
                <div className="contractor-profile__work-grid">
                  {recentWorks.map((work: any, index: number) => (
                    <article key={work.id || `${work.name}-${index}`} className="contractor-profile__work-card">
                      <RecentWorkImage work={work} />
                      <div>
                        <h3>{work.name || "Recent project"}</h3>
                        {work.description ? <p>{work.description}</p> : null}
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="contractor-profile__empty-work"><ImageIcon aria-hidden="true" /><p>No recent work added.</p></div>
              )}
            </section>
          </main>

          <aside className="contractor-profile__sidebar">
            <section className="contractor-profile__panel">
              <div className="contractor-profile__section-heading"><div><span>Business</span><h2>Company details</h2></div></div>
              <DetailRow icon={Building2} label="Company" value={clean(contractor.cc_company_name) || "Not provided"} />
              <DetailRow icon={BriefcaseBusiness} label="Registration" value={clean(contractor.cc_company_registered_number) || "Not provided"} />
            </section>
            <section className="contractor-profile__panel">
              <div className="contractor-profile__section-heading"><div><span>Get in touch</span><h2>Contact information</h2></div></div>
              <DetailRow icon={Phone} label="Phone" value={clean(contractor.cc_phone_number || contractor.user_phone) || "Not provided"} />
              <DetailRow icon={Mail} label="Email" value={clean(contractor.cc_company_email || contractor.user_email) || "Not provided"} />
              <DetailRow icon={MapPin} label="Location" value={address || "Not provided"} />
            </section>
          </aside>
        </div>
      </Container>
    </div>
  );
}

function ProfileStat({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="contractor-profile__stat">
      <span><Icon aria-hidden="true" /></span>
      <div><small>{label}</small><strong>{value}</strong></div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="contractor-profile__detail-row">
      <span><Icon aria-hidden="true" /></span>
      <div><small>{label}</small><p>{value}</p></div>
    </div>
  );
}

function RecentWorkImage({ work }: { work: any }) {
  const [imageFailed, setImageFailed] = useState(false);
  const image = clean(work?.images?.[0]);
  const placeholderImage = clean(work?.placeholder_image);
  const source = image && !imageFailed ? image : placeholderImage;

  return source ? (
    <img
      src={source}
      alt={work?.name || "Recent construction project"}
      onError={() => setImageFailed(true)}
    />
  ) : (
    <div className="contractor-profile__work-placeholder"><ImageIcon aria-hidden="true" /></div>
  );
}
